import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { nanoid } from 'nanoid'
import Cookies from 'js-cookie';

const USER_SECRET_COOKIE = 'chordracer_user_secret';
const COOKIE_EXPIRY_DAYS = 365;

interface UserContextType {
  userId: number | null;
  name: string | null;
  email: string | null;
  completedLevels: Set<number>;
  setLevelCompleted: (level: number) => void;
  updateUser: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userId, setUserId] = useState<number | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [completedLevels, setCompletedLevels] = useState<Set<number>>(new Set());
  //const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      //setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/v1/users/`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          return; 
        }
        throw new Error('Failed to fetch user data');
      }

      const userData = await response.json();
      if (userData.id < 0) {
        return;
      }
      setUserId(userData.id);
      setName(userData.name);
      setEmail(userData.email);

      const completedLevelsData = userData.completed_levels || [];
      setCompletedLevels(new Set(completedLevelsData));
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      //setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Try to get existing secret key from cookie
    let secretKey = Cookies.get(USER_SECRET_COOKIE);

    // If no key exists, create a new one
    if (!secretKey) {
      secretKey = nanoid();
      Cookies.set(USER_SECRET_COOKIE, secretKey, {
        expires: COOKIE_EXPIRY_DAYS,
        sameSite: 'strict'
      });
    }

    fetchUser();
  }, [fetchUser]);

  const setLevelCompleted = useCallback((level: number) => {
    setCompletedLevels(prev => {
      const newSet = new Set(prev);
      newSet.add(level);
      return newSet;
    });
  }, []);

  return (
    <UserContext.Provider value={{ userId, name, email, completedLevels, setLevelCompleted, updateUser: fetchUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};
